import React from 'react';
import { IonButton, IonIcon, IonLabel } from '@ionic/react';
import { arrowUpOutline, arrowDownOutline, documentOutline, repeatOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useCryptoMode from '@/hooks/useCryptoMode';
import { Asset } from '@/types/types';
import './QuickActions.css';

interface QuickActionProps {
  onSend?: () => void;
  onReceive?: () => void;
  onStatement?: () => void;
  onTrade?: () => void;
  style?: React.CSSProperties;
}

export default function QuickActions({ onSend, onReceive, onStatement, onTrade, style }: QuickActionProps) {
  const { t } = useTranslation('components/quickActions');
  const { cryptoMode } = useCryptoMode();
  const supportedAssets: Asset[] = useSelector((state: any) => state.supportedAssets.supportedAssets);
  
  // Check if there's at least one asset available for trade
  const hasTradeableAsset = React.useMemo(() => {
    if (!supportedAssets || supportedAssets.length === 0) return false;
    
    const filteredAssets = supportedAssets.filter((a: Asset) => {
      if (cryptoMode) {
        return (a.type === 'crypto' || a.type === 'fiat') && a.trade_enabled === true;
      } else {
        return a.type === 'fiat' && a.trade_enabled === true;
      }
    });

    // Need at least 2 assets (one to trade from, one to trade to)
    return filteredAssets.length >= 2;
  }, [supportedAssets, cryptoMode]);

  const actions = [
    { 
      icon: arrowUpOutline, 
      label: t('send'), 
      onClick: onSend,
      iconRotation: '45deg', // Rotate send arrow 45 degrees to the right
      show: true
    },
    { 
      icon: arrowDownOutline, 
      label: t('receive'), 
      onClick: onReceive,
      iconRotation: '-45deg', // Rotate receive arrow 45 degrees to the left
      show: true
    },
    { 
      icon: repeatOutline, 
      label: t('trade'), 
      onClick: onTrade,
      show: hasTradeableAsset // Only show if trade is possible
    },
    { 
      icon: documentOutline, 
      label: t('statement'), 
      onClick: onStatement,
      show: true
    },
  ].filter(action => action.show); // Filter out hidden actions

  return (
    <div className="w-full px-4 py-6" style={style}>
      {/* Modern banking-style action buttons using IonButton */}
      <div className="flex justify-between gap-4">
        {actions.map((action, index) => (
          <div
            key={index}
            className="flex flex-col items-center gap-3 flex-1"
          >
            {/* Large IonButton with gradient styling */}
            <IonButton
              shape="round"
              size="small"
              fill="solid"
              onClick={action.onClick}
              className="quick-actions-button w-16 h-16 transform transition-all duration-300 hover:scale-110 hover:shadow-lg hover:-translate-y-1 active:scale-100 active:translate-y-0"
              style={{
                '--color': 'var(--ion-color-primary)',
                '--border-radius': '20px',
                '--ripple-color': 'var(--ion-color-primary-tint)',
                width: '54px',
                height: '54px',
                margin: '0'
              }}
            >
              <IonIcon
                slot="icon-only"
                icon={action.icon}
                style={{
                  fontSize: '20px',
                  color: 'var(--ion-color-primary)',
                  transform: `rotate(${action.iconRotation})`,
                  transition: 'transform 0.2s ease'
                }}
              />
            </IonButton>
            
            {/* Enhanced label styling */}
            <IonLabel className="text-sm font-bold text-center leading-tight tracking-wide text-gray-800 dark:text-gray-200 text-wrap ion-padding">
              {action.label}
            </IonLabel>
          </div>
        ))}
      </div>
    </div>
  );
}